// 语言工具模块 - 提供所有语言相关的通用功能

// 支持的语言列表
export const SUPPORTED_LOCALES = [
  'en', 'zh', 'ja', 'ko', 'es', 'fr', 'de', 'ar', 'th', 'pl', 'tr', 'cs', 'pt'
];

export const DEFAULT_LOCALE = 'en';

// 语言信息映射
export const LOCALE_INFO = {
  en: { name: 'English', flag: '🇺🇸', code: 'en-US' },
  zh: { name: '简体中文', flag: '🇨🇳', code: 'zh-CN' },
  ja: { name: '日本語', flag: '🇯🇵', code: 'ja-JP' },
  ko: { name: '한국어', flag: '🇰🇷', code: 'ko-KR' },
  es: { name: 'Español', flag: '🇪🇸', code: 'es-ES' },
  fr: { name: 'Français', flag: '🇫🇷', code: 'fr-FR' },
  de: { name: 'Deutsch', flag: '🇩🇪', code: 'de-DE' },
  ru: { name: 'Русский', flag: '🇷🇺', code: 'ru-RU' },
  ar: { name: 'العربية', flag: '🇸🇦', code: 'ar-SA' },
  th: { name: 'ไทย', flag: '🇹🇭', code: 'th-TH' },
  pl: { name: 'Polski', flag: '🇵🇱', code: 'pl-PL' },
  tr: { name: 'Türkçe', flag: '🇹🇷', code: 'tr-TR' },
  cs: { name: 'Čeština', flag: '🇨🇿', code: 'cs-CZ' },
  pt: { name: 'Português', flag: '🇵🇹', code: 'pt-PT' },
};

/**
 * 验证语言代码是否支持
 * @param {string} locale 语言代码
 * @returns {boolean}
 */
export function isValidLocale(locale) {
  return SUPPORTED_LOCALES.includes(locale);
}

/**
 * 从URL路径中提取语言设置
 * @param {string} url URL字符串
 * @returns {string|null} 语言代码或null
 */
export function extractLocaleFromUrl(url) {
  try {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    
    // 检查第一个路径段是否是支持的语言
    if (pathSegments.length > 0 && isValidLocale(pathSegments[0])) {
      return pathSegments[0];
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting locale from URL:', error);
    return null;
  }
}

/**
 * 从路径中提取语言代码
 * @param {string} pathname 路径字符串
 * @returns {string} 语言代码
 */
export function extractLocaleFromPathname(pathname) {
  const pathSegments = pathname.split('/').filter(Boolean);
  
  if (pathSegments.length > 0 && isValidLocale(pathSegments[0])) {
    return pathSegments[0];
  }
  
  return DEFAULT_LOCALE;
}

/**
 * 确保URL包含正确的语言前缀
 * @param {string} url URL字符串
 * @param {string} locale 目标语言代码
 * @returns {string} 包含语言前缀的URL
 */
export function ensureLocaleInUrl(url, locale = DEFAULT_LOCALE) {
  try {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    
    // 如果URL已经包含语言前缀，替换它
    if (pathSegments.length > 0 && isValidLocale(pathSegments[0])) {
      pathSegments[0] = locale;
    } else {
      // 如果没有语言前缀，添加它（除非是默认语言）
      if (locale !== DEFAULT_LOCALE) {
        pathSegments.unshift(locale);
      }
    }
    
    // 处理默认语言的特殊情况
    if (locale === DEFAULT_LOCALE && pathSegments.length > 0 && isValidLocale(pathSegments[0])) {
      pathSegments.shift(); // 移除语言前缀
    }
    
    urlObj.pathname = '/' + pathSegments.join('/');
    return urlObj.toString();
  } catch (error) {
    console.error('Error adding locale to URL:', error);
    return url;
  }
}

/**
 * 从Accept-Language头中提取首选语言
 * @param {string} acceptLanguage Accept-Language头的值
 * @returns {string|null} 支持的语言代码或null
 */
export function extractLocaleFromAcceptLanguage(acceptLanguage) {
  if (!acceptLanguage) return null;
  
  try {
    const languages = acceptLanguage.split(',').map(lang => lang.split(';')[0].trim());
    
    for (const lang of languages) {
      const langCode = lang.split('-')[0]; // 提取语言代码部分
      if (isValidLocale(langCode)) {
        return langCode;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting locale from Accept-Language:', error);
    return null;
  }
}

/**
 * 多源语言检测
 * @param {Object} options 检测选项
 * @param {string} [options.url] 当前URL
 * @param {string} [options.referer] Referer头
 * @param {string} [options.acceptLanguage] Accept-Language头
 * @param {string} [options.pathname] 当前路径
 * @returns {string} 检测到的语言代码
 */
export function detectLocale({ url, referer, acceptLanguage, pathname } = {}) {
  // 1. 优先从URL路径中提取
  if (pathname) {
    const localeFromPath = extractLocaleFromPathname(pathname);
    if (localeFromPath !== DEFAULT_LOCALE) {
      return localeFromPath;
    }
  }
  
  // 2. 从完整URL中提取
  if (url) {
    const localeFromUrl = extractLocaleFromUrl(url);
    if (localeFromUrl) {
      return localeFromUrl;
    }
  }
  
  // 3. 从Referer头中提取
  if (referer) {
    const localeFromReferer = extractLocaleFromUrl(referer);
    if (localeFromReferer) {
      return localeFromReferer;
    }
  }
  
  // 4. 从Accept-Language头中提取
  if (acceptLanguage) {
    const localeFromAcceptLang = extractLocaleFromAcceptLanguage(acceptLanguage);
    if (localeFromAcceptLang) {
      return localeFromAcceptLang;
    }
  }
  
  // 5. 返回默认语言
  return DEFAULT_LOCALE;
}

/**
 * 构建本地化的重定向URL
 * @param {string} baseUrl 基础URL
 * @param {string} redirectPath 重定向路径
 * @param {string} locale 语言代码
 * @returns {string} 本地化的重定向URL
 */
export function buildLocalizedRedirectUrl(baseUrl, redirectPath, locale = DEFAULT_LOCALE) {
  const redirectUrl = baseUrl + redirectPath;
  return ensureLocaleInUrl(redirectUrl, locale);
}

/**
 * 获取语言显示名称
 * @param {string} locale 语言代码
 * @returns {string} 显示名称
 */
export function getLocaleName(locale) {
  return LOCALE_INFO[locale]?.name || LOCALE_INFO[DEFAULT_LOCALE].name;
}

/**
 * 获取语言标志
 * @param {string} locale 语言代码
 * @returns {string} 标志emoji
 */
export function getLocaleFlag(locale) {
  return LOCALE_INFO[locale]?.flag || LOCALE_INFO[DEFAULT_LOCALE].flag;
}

/**
 * 获取语言的完整显示文本（标志 + 名称）
 * @param {string} locale 语言代码
 * @returns {string} 完整显示文本
 */
export function getLocaleDisplayText(locale) {
  const info = LOCALE_INFO[locale] || LOCALE_INFO[DEFAULT_LOCALE];
  return `${info.flag} ${info.name}`;
} 